import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PlanSelectionProps } from './types';
import PlanCard from './PlanCard';
import AddonCard from './AddonCard';
import { trackEvent, TrackingEvent } from '@/utils/tracking';
import { reportCustomLog } from '@/utils/logReporter';
import './styles/index.less';

const PlanSelection: React.FC<PlanSelectionProps> = ({
  plans,
  summerPromotion = null,
  firstMonthPromotion = null,
  addonPackages,
  planCategory,
  selectedPlan,
  subscription,
  currentPlanData,
  isLoadingProducts = false,
  processingPayment = false,
  hideActionPanel = false,
  onPlanCategoryChange,
  onPlanSelect,
  onCancelSelection,
  onChangePlan,
  onStripePortalRedirect,
  stripeUrl,
  userId,
  isEnterpriseUser = false,
}) => {
  const { t, i18n } = useTranslation();

  // 夏季促销活动截止日期（按当前语言格式化）
  const summerPromoEndDate = summerPromotion?.endTime
    ? new Intl.DateTimeFormat(i18n.language, { month: 'long', day: 'numeric' }).format(
        new Date(summerPromotion.endTime),
      )
    : null;

  // 首月半价：截止日期 + 剩余天数（取服务端 endTime，不随访问重置——景表法合规）
  const firstMonthEndDate = firstMonthPromotion?.endTime
    ? new Intl.DateTimeFormat(i18n.language, { month: 'long', day: 'numeric' }).format(
        new Date(firstMonthPromotion.endTime),
      )
    : null;
  const firstMonthRemainingDays = firstMonthPromotion?.endTime
    ? Math.max(0, Math.ceil((firstMonthPromotion.endTime - Date.now()) / 86400000))
    : null;

  // 获取当前用户的订阅产品ID和状态
  const currentProductId = currentPlanData?.plan?.product_id || '';
  const subscriptionStatus = currentPlanData?.plan?.status || '';
  const planType = currentPlanData?.type || 'PURCHASE';

  // 企业用户（单独联系充值的付费用户，由 /user/enterprise/check 判定）隐藏「パーソナル」tab，
  // 避免个人版低价对比诱导降级。默认 planCategory 为 'personal'，需在此纠正到 'business'，
  // 否则会停在被隐藏的 tab 上。
  React.useEffect(() => {
    if (isEnterpriseUser && planCategory === 'personal') {
      onPlanCategoryChange('business');
    }
    // 仅在判定结果变化时纠正一次，不干扰用户后续手动切换
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnterpriseUser]);

  // 检查某个计划是否是当前计划（如果订阅状态是CANCELED则不显示为当前计划）
  const isCurrentPlan = (planId: string) => {
    if (subscriptionStatus === 'CANCEL') {
      return false;
    }
    return currentProductId === planId;
  };

  // 检查是否有活跃订阅且需要使用Stripe portal管理
  const hasActiveSubscriptionForStripeManagement = () =>
    planType === 'SUBSCRIPTION' && !!currentPlanData?.plan && planCategory !== 'addon';

  // 检查是否应该显示订阅按钮而不是更改计划按钮
  const shouldShowSubscribeButton = () => planType === 'PURCHASE';
  return (
    <div className="plan-selection space-y-6">
      {/* 夏季促销横幅（仅活动进行中且当前账号有资格时展示；addon 不参与活动） */}
      {summerPromotion && planCategory !== 'addon' && (
        <div
          className="summer-promo-banner rounded-xl px-6 py-3 text-white flex flex-wrap items-center justify-center gap-x-4 gap-y-1"
          style={{ background: 'linear-gradient(90deg, #ff7a45 0%, #ff4d4f 100%)' }}
        >
          <span className="font-bold text-base">🔥 {t('account.summerSaleTitle') || 'Summer Sale'}</span>
          <span className="text-sm">
            {t('account.summerSaleDesc', { percent: summerPromotion.maxPercentOff }) ||
              `Up to ${summerPromotion.maxPercentOff}% off your first subscription`}
          </span>
          {summerPromoEndDate && (
            <span className="text-xs opacity-90">
              {t('account.summerSaleEnds', { date: summerPromoEndDate }) || `Ends ${summerPromoEndDate}`}
            </span>
          )}
        </div>
      )}

      {/* 首月半价横幅（与夏促互斥，服务端时间窗保证；addon 不参与活动） */}
      {!summerPromotion && firstMonthPromotion && planCategory !== 'addon' && (
        <div
          className="summer-promo-banner rounded-xl px-6 py-3 text-white flex flex-wrap items-center justify-center gap-x-4 gap-y-1"
          style={{ background: 'linear-gradient(90deg, #ff7a45 0%, #ff4d4f 100%)' }}
        >
          <span className="font-bold text-base">🔥 {t('account.firstMonthSaleTitle')}</span>
          <span className="text-sm">{t('account.firstMonthSaleDesc')}</span>
          {firstMonthEndDate && (
            <span className="text-xs opacity-90">
              {t('account.firstMonthSaleEnds', { days: firstMonthRemainingDays ?? 0, date: firstMonthEndDate })}
            </span>
          )}
        </div>
      )}

      {/* Plan Category Toggle */}
      <div className="plan-category-toggle flex justify-center">
        <div className="toggle-container bg-gray-100 rounded-lg p-1 inline-flex">
          {!isEnterpriseUser && (
            <button
              onClick={() => onPlanCategoryChange('personal')}
              className={`toggle-button ${planCategory === 'personal' ? 'active' : ''}`}
            >
              {t('account.personal') || 'Personal'}
            </button>
          )}
          <button
            onClick={() => onPlanCategoryChange('business')}
            className={`toggle-button ${planCategory === 'business' ? 'active' : ''}`}
          >
            {t('account.business') || 'Business'}
          </button>
          {!isEnterpriseUser && (
            <button
              onClick={() => onPlanCategoryChange('addon')}
              className={`toggle-button ${planCategory === 'addon' ? 'active' : ''}`}
            >
              {t('account.topupPackage') || 'Top-up package'}
            </button>
          )}
        </div>
      </div>

      {/* Action Buttons：前置到套餐卡片上方，避免选中后按钮在视口外看不到 */}
      {((selectedPlan && !isCurrentPlan(selectedPlan) && !hideActionPanel) ||
        hasActiveSubscriptionForStripeManagement()) && (
        <div className="plan-action-panel bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="panel-content flex items-center justify-between">
            <div className="panel-info">
              <h3 className="text-lg font-semibold text-gray-900">
                {hasActiveSubscriptionForStripeManagement()
                  ? t('account.manageSubscription') || 'Manage your subscription'
                  : planCategory === 'addon'
                    ? t('account.readyToPurchasePackage') || 'Ready to purchase this package?'
                    : shouldShowSubscribeButton()
                      ? t('account.readyToSubscribe') || 'Ready to subscribe to this plan?'
                      : t('account.readyToChangePlan') || 'Ready to change your plan?'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {hasActiveSubscriptionForStripeManagement()
                  ? t('account.redirectToStripePortal') ||
                    'You will be redirected to our billing portal to manage your subscription'
                  : planCategory === 'addon'
                    ? t('account.topupPackageAddedImmediately') ||
                      'This top-up package will be added to your account immediately'
                    : shouldShowSubscribeButton()
                      ? t('account.subscriptionTakeEffectImmediately') ||
                        'Your subscription will take effect immediately'
                      : t('account.newPlanTakeEffectNextCycle') ||
                        'Your new plan will take effect at the start of your next billing cycle'}
              </p>
            </div>
            <div className="panel-actions flex space-x-3">
              {!hasActiveSubscriptionForStripeManagement() && (
                <button onClick={onCancelSelection} className="btn-cancel">
                  {t('account.cancel') || 'Cancel'}
                </button>
              )}
              <button
                onClick={() => {
                  // 所有情况都是跳转到外部支付/管理页面，统一上报 visit_checkout
                  trackEvent(TrackingEvent.VISIT_CHECKOUT, {});
                  reportCustomLog('VisitCheckout', userId);

                  if (hasActiveSubscriptionForStripeManagement()) {
                    // 打开 Stripe 管理页面
                    if (stripeUrl) {
                      window.open(stripeUrl, '_blank');
                    } else {
                      onStripePortalRedirect?.();
                    }
                  } else {
                    // 创建订单并跳转到支付页面
                    onChangePlan();
                  }
                }}
                disabled={processingPayment}
                className={`btn-confirm ${processingPayment ? 'processing' : ''}`}
              >
                {processingPayment ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    <span>{t('account.processing') || 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <span>
                      {hasActiveSubscriptionForStripeManagement()
                        ? t('account.manageSubscription') || 'Manage Subscription'
                        : planCategory === 'addon'
                          ? t('account.payNow') || 'Pay Now'
                          : shouldShowSubscribeButton()
                            ? t('account.subscribeToPlan') || 'Subscribe to Plan'
                            : t('account.changePlan') || 'Change Plan'}
                    </span>
                    <span>→</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      {planCategory === 'addon' ? (
        <div className="plans-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoadingProducts
            ? // 加载骨架屏
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            : addonPackages.map((addon) => (
                <AddonCard
                  key={addon.id}
                  addon={addon}
                  onSelect={() => onPlanSelect(addon.id)}
                  selected={selectedPlan === addon.id}
                />
              ))}
        </div>
      ) : (
        <div className="plans-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoadingProducts
            ? // 加载骨架屏
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded mb-4"></div>
                    <div className="space-y-2 mb-4">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            : plans
                .filter((plan) => {
                  // 对于个人版和商业版，根据计划名称过滤
                  if (planCategory === 'personal') {
                    return !plan.name.includes('Business');
                  } else if (planCategory === 'business') {
                    return plan.name.includes('Business');
                  }
                  // 对于addon，使用原有的category过滤
                  return plan.category === planCategory;
                })
                .map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    isCurrent={isCurrentPlan(plan.id)}
                    onSelect={() => {
                      // 如果有活跃订阅且不是addon，则不允许选择
                      if (!hasActiveSubscriptionForStripeManagement()) {
                        onPlanSelect(plan.id);
                      } else {
                        trackEvent(TrackingEvent.VISIT_CHECKOUT, {});
                        reportCustomLog('VisitCheckout', userId);
                        window.open(stripeUrl, '_blank');
                      }
                    }}
                    selected={selectedPlan === plan.id}
                    disabled={hasActiveSubscriptionForStripeManagement()}
                  />
                ))}
        </div>
      )}
    </div>
  );
};

export default PlanSelection;
